import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import InlineNotice from './InlineNotice';
import { useInlineNotice } from '../contexts/InlineNoticeContext';
import { getCurrencyPrefixFromStorage } from '../utils/numberingSystem';
import ConfirmDialog from './ConfirmDialog';

const ClaimModal = ({ isOpen, onClose, claim, isEditable = false, onClaimSaved, onClaimSubmitted }) => {
  const { showInlineNotice } = useInlineNotice();
  const currencyPrefix = getCurrencyPrefixFromStorage();
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [attachmentName, setAttachmentName] = useState('');
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState('');
  const [attachmentPreviewType, setAttachmentPreviewType] = useState('');
  const [attachmentPreviewName, setAttachmentPreviewName] = useState('');
  const [removeAttachment, setRemoveAttachment] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentUserName, setCurrentUserName] = useState('');
  const [userRole, setUserRole] = useState('user');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  const isEditMode = !!claim;
  const isReadOnly = isEditMode && !isEditable;
  const canViewHistory = isEditMode && (
    ['manager', 'admin', 'appAdmin'].includes(userRole) ||
    (currentUserId && claim?.createdById === currentUserId)
  );

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        setCurrentUserName(userData.name || userData.username || 'User');
        setUserRole(userData.role || 'user');
        setCurrentUserId(userData.id || userData._id || null);
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }, []);

  const formatDateTime = (value) => {
    if (!value) return 'N/A';
    const dateValue = new Date(value);
    if (Number.isNaN(dateValue.getTime())) return 'N/A';
    return dateValue.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const buildHistoryEntries = () => {
    if (!claim) return [];
    const entries = [];
    const createdAt = claim.createdAt;
    const updatedAt = claim.updatedAt;
    const createdByLabel = claim.createdByName || currentUserName || 'Unknown';

    if (createdAt) {
      entries.push({
        action: 'Created',
        dateTime: createdAt,
        author: createdByLabel,
        sortTime: new Date(createdAt).getTime()
      });
    }

    if (claim.submittedAt) {
      entries.push({
        action: 'Submitted',
        dateTime: claim.submittedAt,
        author: createdByLabel,
        sortTime: new Date(claim.submittedAt).getTime()
      });
    }

    if (claim.approvedAt) {
      entries.push({
        action: 'Approved',
        dateTime: claim.approvedAt,
        author: 'Manager',
        sortTime: new Date(claim.approvedAt).getTime()
      });
    }

    if (claim.rejectedAt) {
      entries.push({
        action: 'Rejected',
        dateTime: claim.rejectedAt,
        author: 'Manager',
        sortTime: new Date(claim.rejectedAt).getTime()
      });
    }

    if (updatedAt && createdAt && updatedAt !== createdAt) {
      entries.push({
        action: 'Updated',
        dateTime: updatedAt,
        author: createdByLabel,
        sortTime: new Date(updatedAt).getTime()
      });
    }

    return entries.sort((a, b) => (b.sortTime || 0) - (a.sortTime || 0));
  };

  useEffect(() => {
    if (isOpen) {
      if (claim) {
        setDate(claim.date ? new Date(claim.date).toISOString().split('T')[0] : '');
        setDescription(claim.description || '');
        setAmount(claim.amount?.toString() || '');
        setAttachment(null);
        setAttachmentName(claim.attachment?.originalName || '');
      } else {
        setDate('');
        setDescription('');
        setAmount('');
        setAttachment(null);
        setAttachmentName('');
      }
      setRemoveAttachment(false);
      setShowRemoveConfirm(false);
      setError('');
    }
  }, [isOpen, claim]);

  useEffect(() => {
    if (removeAttachment) {
      setAttachmentPreviewUrl('');
      setAttachmentPreviewType('');
      setAttachmentPreviewName('');
      return undefined;
    }

    if (attachment) {
      const objectUrl = URL.createObjectURL(attachment);
      setAttachmentPreviewUrl(objectUrl);
      setAttachmentPreviewType(attachment.type || '');
      setAttachmentPreviewName(attachment.name || '');
      return () => URL.revokeObjectURL(objectUrl);
    }

    if (claim?.attachmentUrl) {
      setAttachmentPreviewUrl(claim.attachmentUrl);
      setAttachmentPreviewType(claim.attachment?.mimeType || '');
      setAttachmentPreviewName(claim.attachment?.originalName || '');
      return undefined;
    }

    setAttachmentPreviewUrl('');
    setAttachmentPreviewType('');
    setAttachmentPreviewName('');
    return undefined;
  }, [attachment, claim, removeAttachment]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachment(file);
    setAttachmentName(file.name);
    setRemoveAttachment(false);
  };

  const handleRemoveAttachment = () => {
    setAttachment(null);
    setAttachmentName('');
    setRemoveAttachment(true);
    setShowRemoveConfirm(false);
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append('date', date);
    formData.append('description', description);
    formData.append('amount', amount);
    if (removeAttachment) {
      formData.append('removeAttachment', 'true');
    }
    if (attachment) {
      formData.append('attachment', attachment);
    }
    return formData;
  };

  const handleSaveDraft = async (options = { closeOnSave: true }) => {
    if (!date || !description || !amount) {
      setError('Date, description, and amount are required.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const formData = buildFormData();
      const response = isEditMode
        ? await axios.put(`${API_BASE_URL}/claims/${claim._id}`, formData)
        : await axios.post(`${API_BASE_URL}/claims`, formData);

      const label = response.data?.claimNumber || response.data?._id || 'claim';
      showInlineNotice(`Claim ${label} saved successfully.`, {
        scope: 'claim-modal'
      });

      if (onClaimSaved) {
        onClaimSaved(response.data);
      }
      if (options.closeOnSave) {
        onClose();
      }
      return response.data;
    } catch (err) {
      console.error('Error saving claim:', err);
      const message = err.response?.data?.error || 'Failed to save claim';
      setError(message);
      showInlineNotice(message, { scope: 'claim-modal', type: 'error' });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    let claimId = claim?._id;
    if (!isEditMode) {
      const created = await handleSaveDraft({ closeOnSave: false });
      claimId = created?._id;
    }

    if (!claimId) return;

    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_BASE_URL}/claims/${claimId}/submit`);
      showInlineNotice('Claim submitted successfully.', {
        scope: 'claim-modal'
      });
      if (onClaimSubmitted) {
        onClaimSubmitted();
      }
      onClose();
    } catch (err) {
      console.error('Error submitting claim:', err);
      const message = err.response?.data?.error || 'Failed to submit claim';
      setError(message);
      showInlineNotice(message, { scope: 'claim-modal', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? (isReadOnly ? 'View Claim' : 'Edit Claim') : 'New Claim'}</h2>
          <button className="modal-close" onClick={onClose} disabled={loading}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="modal-body" style={{ padding: '24px' }}>
          <InlineNotice scope="claim-modal" />
          {error && (
            <div style={{
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '16px',
              color: '#dc2626',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {isEditMode && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '12px',
              padding: '12px 16px',
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#374151'
            }}>
              <div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Submitted By</div>
                <div style={{ fontWeight: '600' }}>{claim?.createdByName || currentUserName}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Status</div>
                <div style={{ fontWeight: '600' }}>{claim?.status || 'Draft'}</div>
              </div>
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#1f2937' }}>
              Date *
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              disabled={loading || isReadOnly}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: loading || isReadOnly ? '#f3f4f6' : '#fff'
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#1f2937' }}>
              Amount ({currencyPrefix}) *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={loading || isReadOnly}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: loading || isReadOnly ? '#f3f4f6' : '#fff'
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#1f2937' }}>
              Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              disabled={loading || isReadOnly}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: loading || isReadOnly ? '#f3f4f6' : '#fff',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#1f2937' }}>
              Attachment
            </label>
            {claim?.attachmentUrl && !attachmentName && !removeAttachment && (
              <div style={{ marginBottom: '8px', fontSize: '13px' }}>
                <a href={claim.attachmentUrl} target="_blank" rel="noreferrer">
                  View current attachment
                </a>
              </div>
            )}
            {!isReadOnly && (claim?.status || 'Draft') === 'Draft' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <input
                  type="file"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                {(claim?.attachmentUrl || attachmentName) && !removeAttachment && (
                  <button
                    type="button"
                    onClick={() => setShowRemoveConfirm(true)}
                    disabled={loading}
                    style={{
                      alignSelf: 'flex-start',
                      padding: '6px 12px',
                      backgroundColor: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      color: '#111827'
                    }}
                  >
                    Remove attachment
                  </button>
                )}
              </div>
            )}
            {attachmentName && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>{attachmentName}</div>
            )}
            {removeAttachment && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#b45309' }}>
                Attachment will be removed on save.
              </div>
            )}
            {attachmentPreviewUrl && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>Preview</div>
                {(() => {
                  const isPdf = attachmentPreviewType === 'application/pdf'
                    || /\.pdf$/i.test(attachmentPreviewName || '');
                  const isImage = attachmentPreviewType.startsWith('image/')
                    || /\.(png|jpe?g|gif|webp)$/i.test(attachmentPreviewName || '');

                  if (isImage) {
                    return (
                      <img
                        src={attachmentPreviewUrl}
                        alt={attachmentPreviewName || 'Claim attachment'}
                        style={{
                          width: '100%',
                          maxHeight: '320px',
                          objectFit: 'contain',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          backgroundColor: '#f9fafb'
                        }}
                      />
                    );
                  }

                  if (isPdf) {
                    return (
                      <iframe
                        title={attachmentPreviewName || 'Claim attachment'}
                        src={attachmentPreviewUrl}
                        style={{
                          width: '100%',
                          height: '360px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          backgroundColor: '#f9fafb'
                        }}
                      />
                    );
                  }

                  return (
                    <a href={attachmentPreviewUrl} target="_blank" rel="noreferrer">
                      Open attachment
                    </a>
                  );
                })()}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#1f2937' }}>
              Submitted By
            </label>
            <input
              type="text"
              value={claim?.createdByName || currentUserName}
              disabled
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                backgroundColor: '#f3f4f6'
              }}
            />
          </div>

          {canViewHistory && (
            <div style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: '#fff'
            }}>
              <button
                type="button"
                onClick={() => setHistoryExpanded(prev => !prev)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1f2937'
                }}
              >
                <span>History</span>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  {historyExpanded ? 'Hide' : 'Show'}
                </span>
              </button>

              {historyExpanded && (
                <div style={{ marginTop: '12px' }}>
                  {buildHistoryEntries().length === 0 ? (
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>No history yet.</div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                            <th style={{ padding: '8px 6px', color: '#374151' }}>Action</th>
                            <th style={{ padding: '8px 6px', color: '#374151' }}>DateTime</th>
                            <th style={{ padding: '8px 6px', color: '#374151' }}>Author</th>
                          </tr>
                        </thead>
                        <tbody>
                          {buildHistoryEntries().map((entry, index) => (
                            <tr key={`${entry.action}-${index}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '8px 6px', color: '#1f2937' }}>{entry.action}</td>
                              <td style={{ padding: '8px 6px', color: '#374151' }}>{formatDateTime(entry.dateTime)}</td>
                              <td style={{ padding: '8px 6px', color: '#374151' }}>{entry.author}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              color: '#1f2937',
              opacity: loading ? 0.6 : 1
            }}
          >
            Close
          </button>
          {!isReadOnly && (
            <>
              <button
                onClick={handleSaveDraft}
                disabled={loading || !date || !description || !amount}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: (loading || !date || !description || !amount) ? 'not-allowed' : 'pointer',
                  opacity: (loading || !date || !description || !amount) ? 0.6 : 1
                }}
              >
                Save Draft
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || !date || !description || !amount}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: (loading || !date || !description || !amount) ? 'not-allowed' : 'pointer',
                  opacity: (loading || !date || !description || !amount) ? 0.6 : 1
                }}
              >
                Submit
              </button>
            </>
          )}
        </div>
        </div>
      </div>
      <ConfirmDialog
        show={showRemoveConfirm}
        onHide={() => setShowRemoveConfirm(false)}
        onConfirm={handleRemoveAttachment}
        title="Remove attachment"
        message="Remove the current attachment? You can upload a new file before submitting the claim."
        confirmText="Remove"
        cancelText="Keep"
        variant="danger"
      />
    </>
  );
};

export default ClaimModal;
